import { useState } from 'react';
import type { Vehicle, Tag, Dealership } from '../types';
import { PROGRESS_STATUSES, VEHICLE_CLASSES, CATEGORIES } from '../types';

interface Props {
  selected: Set<number>;
  vehicles: Vehicle[];
  tags: Tag[];
  dealerships: Dealership[];
  isPending: boolean;
  onSelectAll: () => void;
  onClear: () => void;
  onBulkPatch: (patch: Partial<Vehicle> & { tag_ids?: number[] }) => void;
  onBulkTagAdd: (tagId: number) => void;
  onBulkTagRemove: (tagId: number) => void;
  onBulkDelete: () => void;
}

// Small reusable "action module" — label + control(s) + apply button
function Module({ label, children, onApply, disabled }: {
  label: string;
  children: React.ReactNode;
  onApply: () => void;
  disabled?: boolean;
}) {
  return (
    <div
      className="flex flex-col gap-1.5 px-3 py-2"
      style={{ borderRight: '1px solid var(--border)' }}
    >
      <span className="font-heading text-[9px] uppercase tracking-widest" style={{ color: 'var(--text-muted)' }}>
        {label}
      </span>
      <div className="flex items-center gap-1.5">
        {children}
        <button
          onClick={onApply}
          disabled={disabled}
          className="px-2 py-1 bg-dark text-white text-[10px] font-heading uppercase hover:bg-primary transition-colors disabled:opacity-30"
        >
          ✓
        </button>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  background: 'var(--input-bg)',
  border: '1px solid var(--input-border)',
  color: 'var(--text)',
  fontFamily: 'Inter, sans-serif',
  fontSize: 11,
  padding: '4px 6px',
  outline: 'none',
  width: '100%',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  minWidth: 120,
  cursor: 'pointer',
};

export default function BulkActionBar({
  selected, vehicles, tags, dealerships, isPending,
  onSelectAll, onClear, onBulkPatch, onBulkTagAdd, onBulkTagRemove, onBulkDelete,
}: Props) {
  // Field states
  const [progress,    setProgress]    = useState('');
  const [category,    setCategory]    = useState('');
  const [vClass,      setVClass]      = useState('');
  const [brand,       setBrand]       = useState('');
  const [dealership,  setDealership]  = useState('');
  const [doors,       setDoors]       = useState('');
  const [seats,       setSeats]       = useState('');
  const [gears,       setGears]       = useState('');
  const [addTagId,    setAddTagId]    = useState('');
  const [removeTagId, setRemoveTagId] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  const count = selected.size;

  const applySpecs = () => {
    const patch: Partial<Vehicle> = {};
    if (doors.trim())  patch.doors = parseInt(doors);
    if (seats.trim())  patch.seats = parseInt(seats);
    if (gears.trim())  patch.gears = parseInt(gears);
    if (Object.keys(patch).length === 0) return;
    onBulkPatch(patch);
    setDoors(''); setSeats(''); setGears('');
  };

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-40 shadow-modal"
      style={{ background: 'var(--card)', borderTop: '2px solid var(--border-strong)' }}
    >
      {/* ── Info row ────────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 px-4 py-2 border-b"
        style={{ background: 'var(--surface)', borderColor: 'var(--border)' }}
      >
        <span className="font-heading text-sm" style={{ color: 'var(--text)' }}>
          {count} vehicle{count !== 1 ? 's' : ''} selected
        </span>

        <button
          onClick={onSelectAll}
          className="text-[11px] font-heading uppercase hover:text-primary transition-colors"
          style={{ color: 'var(--text-muted)' }}
        >
          Select All on Page
        </button>

        <button
          onClick={onClear}
          className="text-[11px] font-heading uppercase hover:text-primary transition-colors"
          style={{ color: 'var(--text-muted)' }}
        >
          Clear
        </button>

        <div className="ml-auto flex items-center gap-2">
          {deleteConfirm ? (
            <>
              <span className="font-body text-xs text-primary">Delete {count} vehicles?</span>
              <button
                onClick={() => { onBulkDelete(); setDeleteConfirm(false); }}
                disabled={isPending}
                className="px-3 py-1 bg-primary text-white text-xs font-heading uppercase hover:bg-primary-dark transition-colors"
              >
                Confirm
              </button>
              <button
                onClick={() => setDeleteConfirm(false)}
                className="px-3 py-1 border text-xs font-heading uppercase transition-colors"
                style={{ borderColor: 'var(--border-strong)', color: 'var(--text)' }}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => setDeleteConfirm(true)}
              className="px-3 py-1 border border-primary text-primary text-xs font-heading uppercase hover:bg-primary hover:text-white transition-colors"
            >
              Delete {count}
            </button>
          )}
        </div>
      </div>

      {/* ── Action modules row ──────────────────────────────────── */}
      <div className="flex flex-wrap overflow-x-auto">

        {/* Progress */}
        <Module label="Progress" onApply={() => { if (progress) { onBulkPatch({ progress: progress as Vehicle['progress'] }); setProgress(''); } }} disabled={!progress || isPending}>
          <select style={selectStyle} value={progress} onChange={e => setProgress(e.target.value)}>
            <option value="">— Select —</option>
            {PROGRESS_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </Module>

        {/* Category */}
        <Module label="Category" onApply={() => { if (category) { onBulkPatch({ category }); setCategory(''); } }} disabled={!category || isPending}>
          <select style={selectStyle} value={category} onChange={e => setCategory(e.target.value)}>
            <option value="">— Select —</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Module>

        {/* Vehicle Class */}
        <Module label="Class" onApply={() => { if (vClass) { onBulkPatch({ vehicle_class: vClass as Vehicle['vehicle_class'] }); setVClass(''); } }} disabled={!vClass || isPending}>
          <select style={{ ...selectStyle, minWidth: 90 }} value={vClass} onChange={e => setVClass(e.target.value)}>
            <option value="">— Select —</option>
            {VEHICLE_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </Module>

        {/* Brand */}
        <Module label="Brand" onApply={() => { if (brand.trim()) { onBulkPatch({ brand: brand.trim() }); setBrand(''); } }} disabled={!brand.trim() || isPending}>
          <input
            style={{ ...inputStyle, width: 110 }}
            placeholder="e.g. BMW"
            value={brand}
            onChange={e => setBrand(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && brand.trim() && onBulkPatch({ brand: brand.trim() })}
          />
        </Module>

        {/* Dealership */}
        <Module label="Dealership" onApply={() => { onBulkPatch({ dealership_id: (dealership ? parseInt(dealership) : null) as unknown as number }); setDealership(''); }} disabled={isPending}>
          <select style={selectStyle} value={dealership} onChange={e => setDealership(e.target.value)}>
            <option value="">— None —</option>
            {dealerships.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
        </Module>

        {/* Doors / Seats / Gears */}
        <Module label="Doors · Seats · Gears" onApply={applySpecs} disabled={(!doors && !seats && !gears) || isPending}>
          {(['D', 'S', 'G'] as const).map((lbl, i) => {
            const val  = [doors, seats, gears][i];
            const set  = [setDoors, setSeats, setGears][i];
            return (
              <div key={lbl} style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <span className="font-heading text-[9px]" style={{ color: 'var(--text-muted)' }}>{lbl}</span>
                <input
                  type="number" min="0" max="99"
                  style={{ ...inputStyle, width: 38, textAlign: 'center' }}
                  placeholder="—"
                  value={val}
                  onChange={e => set(e.target.value)}
                />
              </div>
            );
          })}
        </Module>

        {/* Add Tag */}
        <Module
          label="Add Tag"
          onApply={() => { if (addTagId) { onBulkTagAdd(parseInt(addTagId)); setAddTagId(''); } }}
          disabled={!addTagId || isPending}
        >
          <select style={{ ...selectStyle, minWidth: 110 }} value={addTagId} onChange={e => setAddTagId(e.target.value)}>
            <option value="">— Select —</option>
            {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Module>

        {/* Remove Tag */}
        <Module
          label="Remove Tag"
          onApply={() => { if (removeTagId) { onBulkTagRemove(parseInt(removeTagId)); setRemoveTagId(''); } }}
          disabled={!removeTagId || isPending}
        >
          <select style={{ ...selectStyle, minWidth: 110 }} value={removeTagId} onChange={e => setRemoveTagId(e.target.value)}>
            <option value="">— Select —</option>
            {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </Module>

      </div>
    </div>
  );
}
